export interface BrewRecipe {
  method?: string;
  ratio?: string;
  temp_c?: number;
  time_s?: number;
  notes?: string;
}

export interface CoffeePhoto {
  id: number;
  r2_key: string;
  sort_order: number;
  created_at: string;
}

export interface Coffee {
  id: number;
  kind: string;
  variety: string;
  photos: CoffeePhoto[];
  process: string;
  grams: number;
  country: string;
  region: string | null;
  masl: number | null;
  farm: string | null;
  farmer: string | null;
  roaster: string;
  roaster_city: string | null;
  roast_date: string | null;
  roaster_sku: string | null;
  lot_number: string | null;
  grade: string | null;
  award_detail: string | null;
  award_score: number | null;
  flavor_tags: string[];
  aroma_notes: string | null;
  finish_notes: string | null;
  brew_recipe: BrewRecipe | null;
  personal_notes: string | null;
  story: string | null;
  photo_url: string | null;
  portions: number;
  freeze_date: string | null;
  bean_size: number | null;   // latest measured median screen no. (bean_measurements)
  origin_lat: number | null;  // growing-region (or country) centroid — Stats world map
  origin_lng: number | null;
  roaster_lat: number | null; // roaster city centroid — Stats world map
  roaster_lng: number | null;
  archived: boolean;
  created_at: string;
  updated_at: string;
}

export interface CoffeeInput {
  kind?: string;
  variety: string;
  process: string;
  grams?: number;
  country: string;
  region?: string | null;
  masl?: number | null;
  farm?: string | null;
  farmer?: string | null;
  roaster: string;
  roaster_city?: string | null;
  roast_date?: string | null;
  roaster_sku?: string | null;
  lot_number?: string | null;
  grade?: string | null;
  award_detail?: string | null;
  award_score?: number | null;
  flavor_tags?: string[];
  aroma_notes?: string | null;
  finish_notes?: string | null;
  brew_recipe?: BrewRecipe | null;
  personal_notes?: string | null;
  story?: string | null;
  photo_url?: string | null;
  portions?: number;
  freeze_date?: string | null;
}

export interface PortionLogEntry {
  id: number;
  coffee_id: number;
  delta: number;
  logged_at: string;
  note: string | null;
}

// One brew-log entry. Still called "impression" for continuity; it now carries a 0–100
// cupping score, brew parameters, and the deliberate-practice fields (adjustment / next_time).
export interface Impression {
  id: number;
  coffee_id: number;
  text: string;
  score: number | null;
  method: string | null;
  dose_g: number | null;
  water_in_g: number | null;
  water_out_g: number | null;
  time_s: number | null;
  temp_c: number | null;
  grind: string | null;
  grind_microns: number | null;
  brew_type: string | null;   // 'hot' | 'iced'
  dilution_g: number | null;  // ice/water added for iced brews
  flavor_tags: string[];
  adjustment: string | null;
  next_time: string | null;
  dripper_id: number | null;   // which dripper/brewer was used
  dripper_name: string | null; // resolved name (joined) for display
  grinder_id: number | null;   // which grinder — grind labels are only comparable within one grinder
  grinder_name: string | null; // resolved name (joined) for display
  rpm: number | null;          // grinder speed for this brew
  pour_structure: string | null; // e.g. "2-6-5" (ratio stages) or "50/50/50/40" (g)
  frozen: boolean | null;      // ground from frozen (true) / fresh or thawed (false) / unknown
  logged_at: string;
}

// Payload for logging a brew. At least one of { text, score } is required by the API.
export interface BrewInput {
  text?: string;
  score?: number | null;
  method?: string | null;
  dose_g?: number | null;
  water_in_g?: number | null;
  water_out_g?: number | null;
  time_s?: number | null;
  temp_c?: number | null;
  grind?: string | null;
  grind_microns?: number | null;
  brew_type?: string | null;
  dilution_g?: number | null;
  flavor_tags?: string[];
  adjustment?: string | null;
  next_time?: string | null;
  dripper_id?: number | null;
  grinder_id?: number | null;
  rpm?: number | null;
  pour_structure?: string | null;
  frozen?: boolean | null;
}

// Cross-coffee brew feed row from GET /api/brews — a brew entry plus the coffee fields
// needed for grouping on the Stats page and for recipe-intelligence suggestions.
export interface BrewFeedEntry extends Impression {
  variety: string;
  process: string;
  country: string;
  roaster: string;
  kind: string;
}

export interface RoasterColor {
  roaster: string;
  bg_color: string;   // #rrggbb
  text_color: string; // #rrggbb
  bold: boolean;
  italic: boolean;
  updated_at: string;
}

// ── Drippers — user-curated brewer catalog ──────────────────────────────────────
export const DRIPPER_MATERIALS = ["plastic", "stainless", "glass", "ceramic"] as const;
export const DRIPPER_GEOMETRIES = ["flat", "conical"] as const;

export interface Dripper {
  id: number;
  name: string;
  brand: string | null;
  material: string | null;   // plastic | stainless | glass | ceramic (free text)
  geometry: string | null;   // flat | conical (free text)
  url: string | null;
  image_url: string | null;
  notes: string | null;
  active: boolean;
  created_at: string;
  updated_at: string;
}

export interface DripperInput {
  name: string;
  brand?: string | null;
  material?: string | null;
  geometry?: string | null;
  url?: string | null;
  image_url?: string | null;
  notes?: string | null;
  active?: boolean;
}

// ── Grinders — user-curated grinder catalog ─────────────────────────────────────
export interface Grinder {
  id: number;
  name: string;
  brand: string | null;
  burr: string | null;       // free text: "SSP MP", "stock Mizen"…
  burr_mm: number | null;    // burr diameter
  rpm_min: number | null;    // fixed-speed grinders: rpm_min = rpm_max
  rpm_max: number | null;
  url: string | null;
  notes: string | null;
  acquired_on: string | null; // YYYY-MM-DD
  retired_on: string | null;  // YYYY-MM-DD
  // Starting-grind heuristic on this grinder: base = washed, fresh bag; offsets add (COFFEE_PHYSICS §3/§12).
  grind_base: number | null;
  off_frozen: number | null;
  off_natural: number | null;
  off_small_bean: number | null;
  active: boolean;
  created_at: string;
  updated_at: string;
}

export interface GrinderInput {
  name: string;
  brand?: string | null;
  burr?: string | null;
  burr_mm?: number | null;
  rpm_min?: number | null;
  rpm_max?: number | null;
  url?: string | null;
  notes?: string | null;
  acquired_on?: string | null;
  retired_on?: string | null;
  grind_base?: number | null;
  off_frozen?: number | null;
  off_natural?: number | null;
  off_small_bean?: number | null;
  active?: boolean;
}

// ── Café Log — out-of-home coffee experience journal ────────────────────────────
export interface CafeLog {
  id: number;
  cafe_name: string;
  cafe_city: string | null;
  visited_on: string | null;
  kind: string;
  variety: string | null;
  process: string | null;
  country: string | null;
  region: string | null;
  roaster: string | null;
  method: string | null;
  dose_g: number | null;
  ratio: string | null;
  temp_c: number | null;
  brew_time_s: number | null;
  grind_size: string | null;
  pours: number | null;
  score: number | null;
  taste_notes: string | null;
  flavor_tags: string[];
  photos: CoffeePhoto[];
  created_at: string;
  updated_at: string;
}

export interface CafeLogInput {
  cafe_name: string;
  cafe_city?: string | null;
  visited_on?: string | null;
  kind?: string;
  variety?: string | null;
  process?: string | null;
  country?: string | null;
  region?: string | null;
  roaster?: string | null;
  method?: string | null;
  dose_g?: number | null;
  ratio?: string | null;
  temp_c?: number | null;
  brew_time_s?: number | null;
  grind_size?: string | null;
  pours?: number | null;
  score?: number | null;
  taste_notes?: string | null;
  flavor_tags?: string[];
}

// ── Bean size measurements — coin-scaled geometry from an on-device photo measurement ─
export interface PerBean {
  len: number;    // major axis (length), mm
  width: number;  // minor axis (width), mm
  aspect: number; // len / width
  screen: number; // width in 1/64 inch
}

export interface BeanMeasurement {
  id: number;
  coffee_id: number;
  measured_at: string;
  n: number;
  median_len_mm: number | null;
  median_width_mm: number | null;
  median_aspect: number | null;
  median_screen: number | null;
  spread_width_mm: number | null;
  scale_mm_px: number | null;
  coin_mm: number | null;
  selftest_circle_mm: number | null;
  per_bean: PerBean[];
  method_version: string | null;
  note: string | null;
}

export interface BeanMeasurementInput {
  n: number;
  median_len_mm?: number | null;
  median_width_mm?: number | null;
  median_aspect?: number | null;
  median_screen?: number | null;
  spread_width_mm?: number | null;
  scale_mm_px?: number | null;
  coin_mm?: number | null;
  selftest_circle_mm?: number | null;
  per_bean?: PerBean[];
  method_version?: string | null;
  note?: string | null;
}

export const BREW_METHOD_OPTIONS = [
  "Espresso", "V60", "Kalita", "Aeropress", "Batch", "French Press", "Chemex", "Origami", "Siphon",
] as const;

export const PROCESS_OPTIONS = [
  "Washed", "Natural", "Honey", "Anaerobic", "Extended Fermentation",
] as const;

export const GRADE_TAGS = ["COE", "BOP", "TOH", "Auction", "Competition", "Micro-lot", "Rare", "Award"] as const;

export const KIND_OPTIONS = ["coffee", "matcha", "gyokuro", "sencha", "hojicha"] as const;
export type Kind = typeof KIND_OPTIONS[number];

const TEA_KIND_SET = new Set<string>(["matcha", "gyokuro", "sencha", "hojicha"]);
export function isTea(kind: string | undefined | null): boolean {
  return !!kind && TEA_KIND_SET.has(kind);
}

export function kindLabel(kind: string | undefined | null): string {
  if (!kind) return "Coffee";
  return kind.charAt(0).toUpperCase() + kind.slice(1);
}

// Tea-specific grade tags. The producer-side grade vocabulary differs from coffee's
// COE/BOP/TOH; matcha is typically marketed as Ceremonial / Premium / Culinary.
export const TEA_GRADE_TAGS = ["Ceremonial", "Premium", "Culinary", "Single Cultivar"] as const;
